import { useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Grid,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useLegacyCase } from "../../../hooks/useLegacyCases";
import LegacyDocumentPanel from "../../../components/admin/legacy-cases/LegacyDocumentPanel";
import LegacyReviewWorkspace from "../../../components/admin/legacy-cases/LegacyReviewWorkspace";
import LegacyRequisitionAttachment from "../../../components/admin/legacy-cases/LegacyRequisitionAttachment";
import LegacyReviewedNarratives from "../../../components/admin/legacy-cases/LegacyReviewedNarratives";
import LegacyCaseCollisionNotice from "../../../components/admin/legacy-cases/LegacyCaseCollisionNotice";
import { useLegacyCaseCollision } from "../../../hooks/useLegacyCaseCollision";

export default function LegacyCaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const query = useLegacyCase(id);
  const collision = useLegacyCaseCollision(id);
  const handleRetry = useCallback(() => {
    void query.refetch();
  }, [query]);
  const handleRefreshCollision = useCallback(() => {
    void collision.refetch();
  }, [collision]);
  const handleCheckImportCollision = useCallback(async () => {
    const refreshed = await collision.refetch();
    return !refreshed.data?.importBlocked;
  }, [collision]);
  const collisionNotice = (
    <LegacyCaseCollisionNotice
      collision={collision.data}
      documents={query.data?.documents ?? []}
      error={collision.error}
      loading={collision.isFetching}
      onRefresh={handleRefreshCollision}
    />
  );
  if (query.isLoading)
    return (
      <Stack role="status" aria-label="Loading archived case">
        {collisionNotice}
        <Text>Preparing the archived case and original report for review…</Text>
        <Skeleton height={48} />
        <Skeleton height={480} />
      </Stack>
    );
  if (!query.data || query.error)
    return (
      <Stack>
        {collisionNotice}
        <Alert color="red" title="Legacy case unavailable">
          <Stack align="flex-start">
            <Text>
              {query.error?.message ||
                "This archive record could not be found."}
            </Text>
            <Button onClick={handleRetry}>Retry</Button>
            <Anchor component={Link} to="/admin/legacy-cases">
              Back to Legacy Cases
            </Anchor>
          </Stack>
        </Alert>
      </Stack>
    );
  return (
    <Stack gap="lg">
      {collisionNotice}
      <Breadcrumbs>
        <Anchor component={Link} to="/admin/legacy-cases">
          Legacy Cases
        </Anchor>
        <Text>{query.data.caseNumber}</Text>
      </Breadcrumbs>
      <Group justify="space-between">
        <Group>
          <Title order={1}>{query.data.caseNumber}</Title>
          <Badge variant="light">NovoPath</Badge>
        </Group>
        {query.data.importedCaseId ? (
          <Group>
            <Button
              component={Link}
              variant="default"
              to={`/cases/${query.data.importedCaseId}`}
            >
              Open imported case
            </Button>
            <Button
              component={Link}
              to={`/admin/legacy-cases/reports/${query.data.importedCaseId}`}
            >
              Review report reconstruction
            </Button>
          </Group>
        ) : (
          <LegacyRequisitionAttachment archiveId={query.data.id} />
        )}
      </Group>
      {query.data.preparationError && (
        <Alert color="orange" title="Review the original report">
          {query.data.preparationError}
        </Alert>
      )}
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Box pos="sticky" top={88}>
            <LegacyDocumentPanel
              documents={query.data.documents}
              initialDocumentId={query.data.latestReview?.data.reportDocumentId}
            />
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 6 }}>
          {query.data.importedCaseId && query.data.latestReview ? (
            <Stack>
              <Alert color="green" title="Imported">
                This historical case is available in the active LIS. The
                imported review is read-only.
              </Alert>
              <LegacyReviewedNarratives review={query.data.latestReview.data} />
            </Stack>
          ) : (
            <LegacyReviewWorkspace
              key={query.data.id}
              detail={query.data}
              importBlocked={collision.data?.importBlocked}
              checkImportCollision={handleCheckImportCollision}
            />
          )}
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
